// ===================================================================
// KAIXO - CRMPipelineWeb
// ===================================================================
// Vista kanban de la pestaña "Contactos" del CRMModal, web-only.
// El CRMModal padre se encarga de: cargar/actualizar/crear/editar leads.
// Este componente se limita a presentar y a soportar drag & drop entre
// columnas (cuando se suelta, llama al callback onStatusChange).
//
// En móvil esta vista no se usa — el CRMModal renderiza CRMLeads (lista).
// ===================================================================

import React, { useEffect, useState } from 'react';
import ct from './i18n';
import './CRMPipelineWeb.css';

const STAGES = ['nuevo', 'contactado', 'interesado', 'propuesta', 'cerrado'];

const AUX_LABELS = {
    español: { empty:'Sin leads', refresh:'Actualizar', allLeads:'Todos los leads', newLead:'Nuevo' },
    english: { empty:'No leads',   refresh:'Refresh',    allLeads:'All leads',      newLead:'New' }
};
function getLang() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return u.language || 'español';
    } catch { return 'español'; }
}
function aux() { return AUX_LABELS[getLang()] || AUX_LABELS.español; }

function relativeDate(iso, lang) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        if (days === 0) return lang === 'english' ? 'today' : 'hoy';
        if (days === 1) return lang === 'english' ? 'yest.' : 'ayer';
        if (days < 30) return `${days}d`;
        return `${Math.floor(days / 30)}m`;
    } catch { return ''; }
}
function formatMoney(v, currency) {
    if (typeof v !== 'number' || !isFinite(v) || v === 0) return '';
    const c = (currency || 'EUR').toUpperCase();
    const sym = c === 'USD' ? '$' : c === 'EUR' ? '€' : c + ' ';
    try { return sym + v.toLocaleString('es-ES'); } catch { return sym + v; }
}

export default function CRMPipelineWeb({
    leads = [],
    loading,
    onReload,
    onEditLead,
    onNewLead,
    onStatusChange  // (lead, newStatus) => Promise
}) {
    const [draggingId, setDraggingId] = useState(null);
    const [hoverStage, setHoverStage] = useState(null);
    // Estado local optimistic — se mezcla con leads para reflejar el drag
    // antes de que llegue la respuesta del backend / socket realtime.
    const [pendingMoves, setPendingMoves] = useState({}); // leadId → newStatus

    // Si el padre nos da leads frescos, podemos limpiar pendingMoves cuyas
    // entradas ya estén reflejadas (lead.status === pending value).
    useEffect(() => {
        setPendingMoves(prev => {
            const next = {};
            for (const [id, status] of Object.entries(prev)) {
                const lead = leads.find(l => String(l._id) === id);
                if (lead && lead.status !== status) next[id] = status;
            }
            return next;
        });
    }, [leads]);

    const labels = aux();
    const lang = getLang();

    // Distribuir leads por etapa, respetando pendingMoves para optimistic UI.
    const buckets = {};
    for (const s of STAGES) buckets[s] = [];
    for (const lead of leads) {
        const stage = pendingMoves[String(lead._id)] || lead.status;
        if (buckets[stage] !== undefined) {
            buckets[stage].push(lead);
        } else if (stage === 'negociacion') {
            // 'negociacion' se agrupa con 'propuesta' en el kanban —
            // ambas son "en proceso" y comparten columna.
            buckets.propuesta.push(lead);
        }
        // 'perdido' no se muestra en el kanban (estado terminal negativo)
    }

    const onDragStart = (leadId) => (e) => {
        setDraggingId(leadId);
        try { e.dataTransfer.effectAllowed = 'move'; } catch {}
    };
    const onDragEnd = () => { setDraggingId(null); setHoverStage(null); };
    const onDragOver = (stage) => (e) => {
        e.preventDefault();
        try { e.dataTransfer.dropEffect = 'move'; } catch {}
        if (hoverStage !== stage) setHoverStage(stage);
    };
    const onDrop = (stage) => async (e) => {
        e.preventDefault();
        const leadId = draggingId;
        setDraggingId(null);
        setHoverStage(null);
        if (!leadId) return;
        const lead = leads.find(l => String(l._id) === String(leadId));
        if (!lead || lead.status === stage) return;
        // Optimistic UI antes de esperar al backend
        setPendingMoves(prev => ({ ...prev, [String(leadId)]: stage }));
        try {
            if (typeof onStatusChange === 'function') {
                await onStatusChange(lead, stage);
            }
        } catch (err) {
            console.warn('CRMPipelineWeb onStatusChange:', err?.message);
            // Quitar el optimistic — la próxima recarga lo aclarará.
            setPendingMoves(prev => {
                const next = { ...prev };
                delete next[String(leadId)];
                return next;
            });
        }
    };

    const totalLeads = STAGES.reduce((acc, s) => acc + buckets[s].length, 0);

    return (
        <div className="crm-pipe-root">
            <div className="crm-pipe-toolbar">
                <span className="crm-pipe-meta">
                    {labels.allLeads} · {ct.total}: <strong>{totalLeads}</strong>
                </span>
                <div className="crm-pipe-actions">
                    {onReload && (
                        <button type="button" className="crm-pipe-btn" onClick={onReload} disabled={loading}>
                            {loading ? '…' : '↻'} {labels.refresh}
                        </button>
                    )}
                    {onNewLead && (
                        <button type="button" className="crm-pipe-btn primary" onClick={onNewLead}>
                            + {labels.newLead}
                        </button>
                    )}
                </div>
            </div>

            <div className="crm-pipe-board">
                {STAGES.map(stage => {
                    const items = buckets[stage] || [];
                    const label = (ct.statuses && ct.statuses[stage]) || stage;
                    const isWon = stage === 'cerrado';
                    return (
                        <div
                            key={stage}
                            className={`crm-pipe-col ${hoverStage === stage ? 'drop-target' : ''} ${isWon ? 'col-won' : ''}`}
                            onDragOver={onDragOver(stage)}
                            onDrop={onDrop(stage)}
                            onDragLeave={() => hoverStage === stage && setHoverStage(null)}
                        >
                            <div className="crm-pipe-col-head">
                                <span className="col-name">{label}</span>
                                <span className="col-count">{items.length}</span>
                            </div>
                            <div className="crm-pipe-col-body">
                                {items.length === 0 && (
                                    <div className="crm-pipe-empty">{labels.empty}</div>
                                )}
                                {items.map(lead => {
                                    const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '—';
                                    const money = formatMoney(lead.budget, lead.currency);
                                    return (
                                        <div
                                            key={lead._id}
                                            className={`crm-pipe-card ${draggingId === lead._id ? 'dragging' : ''}`}
                                            draggable
                                            onDragStart={onDragStart(lead._id)}
                                            onDragEnd={onDragEnd}
                                            onClick={() => onEditLead && onEditLead(lead)}
                                            title={fullName}
                                        >
                                            <div className="card-name">{fullName}</div>
                                            <div className="card-meta">
                                                {lead.source && <span className="card-source">{lead.source}</span>}
                                                {lead.source && (lead.product || money) && <span className="dot-sep">·</span>}
                                                {lead.product && <span className="card-product">{lead.product}</span>}
                                                {money && <span className="dot-sep">·</span>}
                                                {money && <span className="card-money">{money}</span>}
                                            </div>
                                            {/* Próxima acción si existe — pequeña etiqueta debajo
                                                de meta para que el usuario vea de un vistazo qué
                                                tiene pendiente con ese lead sin abrir el detalle. */}
                                            {lead.nextAction && lead.nextAction.at && !lead.nextAction.completed && (() => {
                                                const at = new Date(lead.nextAction.at);
                                                const isOverdue = at < new Date();
                                                const day = at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
                                                const time = at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                                                return (
                                                    <div className={`card-action ${isOverdue ? 'overdue' : ''}`}>
                                                        {isOverdue ? '⚠ ' : '⏰ '}
                                                        {lead.nextAction.type === 'call' ? '📞' : ''}
                                                        {' ' + day + ' ' + time}
                                                    </div>
                                                );
                                            })()}
                                            <div className="card-foot">
                                                {lead.assignedToName && <span className="card-assigned">{lead.assignedToName}</span>}
                                                <span className="card-date">{relativeDate(lead.updatedAt || lead.createdAt, lang)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
